import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { AnimatePresence, motion } from 'motion/react';
import {
  DocumentCheckIcon,
  DocumentIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  PaperClipIcon,
  TableCellsIcon,
  XMarkIcon
} from '@heroicons/react/20/solid';

import { KnowledgeGraphEntityType } from '~/api/graphql';
import { entityTypeIcon } from '~/components/ams-picker/entity-display';
import type { CaraAttachment, CaraFileAttachment } from '~/components/cara-input/cara-attachment';
import { formatFileSize, splitFileNameExtension } from '~/components/cara-input/cara-attachment';
import type { CaraAttachmentError } from '~/components/cara-input/use-attachment-validation';

const CHIP_BASE_CLASS = clsx(
  'group flex flex-row items-center gap-1.5',
  'max-w-[14rem] rounded-full px-2.5 py-1',
  'text-sm transition-colors',
  // The chip itself is focusable so keyboard users can use the close button
  // without hunting for it. The focus ring is provided by `focus-visible`.
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
);

const CHIP_OK_CLASS = clsx(
  'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300',
  'hover:bg-neutral-200/70 dark:hover:bg-white/10'
);

const CHIP_ERROR_CLASS = clsx(
  'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 ring-1 ring-inset ring-red-200 dark:ring-red-500/30',
  'hover:bg-red-100/70 dark:hover:bg-red-500/20'
);

const CHIP_ICON_OK_CLASS = 'size-3.5 shrink-0 text-neutral-500 dark:text-neutral-400';
const CHIP_ICON_ERROR_CLASS = 'size-3.5 shrink-0 text-red-600 dark:text-red-400';

const CHIP_LABEL_CLASS = 'm-0 truncate font-body leading-normal';

const CHIP_SECONDARY_OK_CLASS = 'shrink-0 text-xs text-neutral-500 dark:text-neutral-400';
const CHIP_SECONDARY_ERROR_CLASS = 'shrink-0 text-xs text-red-500 dark:text-red-400';

const CHIP_REMOVE_OK_CLASS = clsx(
  'inline-flex shrink-0 items-center justify-center rounded-full p-0.5',
  'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-white/10 hover:text-neutral-700 dark:hover:text-neutral-300',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
);
const CHIP_REMOVE_ERROR_CLASS = clsx(
  'inline-flex shrink-0 items-center justify-center rounded-full p-0.5',
  'text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-500/20 hover:text-red-700 dark:hover:text-red-300',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
);

/**
 * Renders the queue of attachments next to the action buttons. Designed for
 * the empty `flex-grow` slot inside the action row of `CaraInput`, so it
 * wraps inline as the user adds more entries instead of pushing the buttons
 * around.
 *
 * Errors (from `useAttachmentValidation`) are looked up per-chip via the
 * `errors` prop, so an empty or oversize file shows up as a clearly broken
 * chip — not just as inline text below the input. The `Map` is built once
 * per render to avoid O(n*m) lookups when many chips are present.
 *
 * Different attachment kinds get different icons + labels so the user can
 * tell files apart from library picks at a glance. Adding a new kind means
 * adding one arm to the switch in `describeAttachment`.
 */
export const CaraInputAttachments = (props: {
  attachments: CaraAttachment[];
  errors?: CaraAttachmentError[];
  onRemove: (id: string) => void;
}) => {
  const errorsById = React.useMemo(() => {
    const m = new Map<string, CaraAttachmentError>();
    for (const err of props.errors ?? []) {
      // First error per attachment wins — typically the validator only
      // emits one anyway, but this keeps behavior predictable.
      if (!m.has(err.attachmentId)) m.set(err.attachmentId, err);
    }
    return m;
  }, [props.errors]);

  return (
    <div
      role="list"
      aria-label="Attached items"
      className="flex flex-row flex-wrap items-center gap-1.5"
    >
      <AnimatePresence initial={false}>
        {props.attachments.map((attachment) => (
          <motion.div
            key={attachment.id}
            role="listitem"
            layout
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.12, ease: 'easeOut' }}
          >
            <AttachmentChip
              attachment={attachment}
              error={errorsById.get(attachment.id)}
              onRemove={props.onRemove}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

const AttachmentChip = ({
  attachment,
  error,
  onRemove
}: {
  attachment: CaraAttachment;
  error: CaraAttachmentError | undefined;
  onRemove: (id: string) => void;
}) => {
  const hasError = !!error;
  const meta = describeAttachment(attachment);
  const LeadingIcon = hasError ? ExclamationTriangleIcon : meta.Icon;
  // Tooltip prefers the error message when broken so a hover/focus tells the
  // user *why* the chip is red without forcing them to look down at the
  // error list below the action row.
  const tooltip = hasError ? error.message : meta.title;

  return (
    <div
      className={clsx(CHIP_BASE_CLASS, hasError ? CHIP_ERROR_CLASS : CHIP_OK_CLASS)}
      title={tooltip}
      aria-label={tooltip}
    >
      <LeadingIcon className={hasError ? CHIP_ICON_ERROR_CLASS : CHIP_ICON_OK_CLASS} />
      <AttachmentChipBody attachment={attachment} hasError={hasError} />
      <Headless.Button
        type="button"
        aria-label={`Remove ${meta.title}`}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(attachment.id);
        }}
        className={hasError ? CHIP_REMOVE_ERROR_CLASS : CHIP_REMOVE_OK_CLASS}
      >
        <XMarkIcon className="size-3" />
      </Headless.Button>
    </div>
  );
};

/**
 * Renders the chip's text content. Files get smart filename truncation
 * (extension preserved) plus a secondary size label; library picks just
 * render their title with end-truncation.
 */
const AttachmentChipBody = ({
  attachment,
  hasError
}: {
  attachment: CaraAttachment;
  hasError: boolean;
}) => {
  if (attachment.kind === 'file') {
    return <FileChipBody attachment={attachment} hasError={hasError} />;
  }
  return <span className={CHIP_LABEL_CLASS}>{describeAttachment(attachment).title}</span>;
};

const FileChipBody = ({
  attachment,
  hasError
}: {
  attachment: CaraFileAttachment;
  hasError: boolean;
}) => {
  const { base, ext } = splitFileNameExtension(attachment.file.name);
  const sizeClass = hasError ? CHIP_SECONDARY_ERROR_CLASS : CHIP_SECONDARY_OK_CLASS;

  return (
    <div className="flex min-w-0 flex-1 items-baseline gap-1">
      {/* Middle-truncation: the base shrinks with `truncate`; the extension
          is pinned with `shrink-0` so `report.pdf` never becomes `repor…`. */}
      <span className={clsx('flex min-w-0', CHIP_LABEL_CLASS)}>
        <span className="truncate">{base}</span>
        {ext && <span className="shrink-0">{ext}</span>}
      </span>
      <span className={sizeClass}>{formatFileSize(attachment.file.size)}</span>
    </div>
  );
};

type ChipIcon = React.ComponentType<{ className?: string }>;

type AttachmentChipMetadata = {
  Icon: ChipIcon;
  /** Full text for the tooltip / aria-label; the rendered label may be truncated. */
  title: string;
};

const entityTypeChipIcon = (entityType: KnowledgeGraphEntityType): ChipIcon => {
  const EntityTypeChipIcon = ({ className }: { className?: string }) => (
    <>{entityTypeIcon(entityType, className ?? CHIP_ICON_OK_CLASS)}</>
  );
  return EntityTypeChipIcon;
};

/**
 * Maps each attachment kind to its leading icon component and full
 * descriptive title. Returning the icon as a component (rather than a
 * pre-instantiated element) lets the chip swap in `ExclamationTriangleIcon`
 * on error and apply size/color via a single className.
 *
 * Keep this exhaustive — the `never` fallthrough guarantees a compile error
 * if a new kind is added to the union without updating the renderer.
 */
const describeAttachment = (attachment: CaraAttachment): AttachmentChipMetadata => {
  switch (attachment.kind) {
    case 'file':
      return { Icon: PaperClipIcon, title: attachment.file.name };
    case 'custom-form':
      return { Icon: DocumentCheckIcon, title: attachment.title };
    case 'word-template':
      return { Icon: DocumentIcon, title: attachment.title };
    case 'excel-template':
      return { Icon: TableCellsIcon, title: attachment.title };
    case 'html-template':
      return { Icon: DocumentTextIcon, title: attachment.title };
    case 'ams-document':
      return {
        Icon: entityTypeChipIcon(KnowledgeGraphEntityType.File),
        title: attachment.displayName
      };
    case 'ams-context-pin':
      return {
        Icon: entityTypeChipIcon(attachment.entityType),
        title: attachment.displayName
      };
    default: {
      const _exhaustive: never = attachment;
      return _exhaustive;
    }
  }
};
